import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Attempt, BookmarkRecord, DashboardData, ProgressRecord, ProposalDraft, SyncResult, Track } from './types';

interface PrzedRejsemDB extends DBSchema {
  attempts: { key: string; value: Attempt; indexes: { 'by-completed': string } };
  progress: { key: string; value: ProgressRecord; indexes: { 'by-track': string } };
  bookmarks: { key: string; value: BookmarkRecord };
  proposals: { key: string; value: ProposalDraft };
  settings: { key: string; value: { key: string; value: unknown } };
}

const databases = new Map<string, Promise<IDBPDatabase<PrzedRejsemDB>>>();
function database(userId: string | null) {
  // The pre-isolation database has unknown ownership. Never expose it as today's guest.
  const name = userId === 'legacy' ? 'przedrejsem-v1' : userId ? 'przedrejsem-account-' + userId : 'przedrejsem-guest-v2';
  if (!databases.has(name)) databases.set(name, openDB<PrzedRejsemDB>(name, 1, {
    upgrade(db) {
      const attempts = db.createObjectStore('attempts', { keyPath: 'id' }); attempts.createIndex('by-completed', 'completedAt');
      const progress = db.createObjectStore('progress', { keyPath: 'key' }); progress.createIndex('by-track', 'track');
      db.createObjectStore('bookmarks', { keyPath: 'key' });
      db.createObjectStore('proposals', { keyPath: 'id' }); db.createObjectStore('settings', { keyPath: 'key' });
    }
  }));
  return databases.get(name)!;
}
let activeUser: string | null = null;
const epochKey = (userId: string | null) => 'przedrejsem:epoch:' + (userId || 'guest');
const pauseKey = (userId: string) => 'przedrejsem:sync-paused:' + userId;
export const syncPaused = (userId: string) => localStorage.getItem(pauseKey(userId)) === 'true';
export function resumeSync(userId: string) { localStorage.removeItem(pauseKey(userId)); changed(userId, 'local'); }
type LearningChange = { userId: string | null; source: 'local' | 'remote' | 'cleared' };
const learningListeners = new Set<(change: LearningChange) => void>();
export function subscribeLearning(listener: (change: LearningChange) => void) {
  learningListeners.add(listener); return () => { learningListeners.delete(listener); };
}
function changed(userId: string | null, source: LearningChange['source']) {
  learningListeners.forEach((listener) => listener({ userId, source }));
}
if (typeof window !== 'undefined') window.addEventListener('storage', event => {
  if (event.key?.startsWith('przedrejsem:sync-paused:')) changed(event.key.slice('przedrejsem:sync-paused:'.length), event.newValue === 'true' ? 'cleared' : 'local');
});
const currentDatabase = () => database(activeUser);
const bookmarkOrder = (row: BookmarkRecord) => new Date(row.updatedAt).getTime().toString().padStart(16, '0') + row.changeId;
function sameAttempt(a: Attempt, b: Attempt) {
  const snapshot = (row: Attempt) => ({ kind: row.kind, track: row.track, bankVersion: row.bankVersion,
    questions: row.questions.map((item) => ({ questionId: item.questionId, revision: item.revision, familyId: item.familyId, optionOrder: item.optionOrder })),
    answers: Object.entries(row.answers).filter(([, value]) => value !== undefined).sort(([a], [b]) => a.localeCompare(b)), flagged: row.flagged,
    startedAt: new Date(row.startedAt).toISOString(), endAt: row.endAt ? new Date(row.endAt).toISOString() : null, completedAt: row.completedAt ? new Date(row.completedAt).toISOString() : null });
  return JSON.stringify(snapshot(a)) === JSON.stringify(snapshot(b));
}

// Capture the account before awaiting; switching accounts cannot redirect an in-flight write.
export function accountStore(userId: string | null) {
  const epoch = localStorage.getItem(epochKey(userId));
  const assertCurrent = () => { if (localStorage.getItem(epochKey(userId)) !== epoch) throw new Error('local_data_cleared'); };
  const get = async () => { const db = await database(userId); assertCurrent(); return db; };
  return {
    assertCurrent,
    async counts() {
      const db = await get(); const names = ['attempts', 'progress', 'bookmarks', 'proposals', 'settings'] as const;
      return Object.fromEntries(await Promise.all(names.map(async name => [name, await db.count(name)]))) as Record<typeof names[number], number>;
    },
    async snapshot(): Promise<DashboardData> {
      const db = await get(); const tx = db.transaction(['attempts', 'progress', 'bookmarks']);
      const [attempts, progress, bookmarks] = await Promise.all([tx.objectStore('attempts').getAll(), tx.objectStore('progress').getAll(), tx.objectStore('bookmarks').getAll()]);
      return { attempts, progress, bookmarks };
    },
    async deviceId() {
      const db = await get(); const tx = db.transaction('settings', 'readwrite');
      let id = (await tx.store.get('device-id'))?.value as string | undefined;
      if (!id) { id = crypto.randomUUID(); await tx.store.put({ key: 'device-id', value: id }); }
      await tx.done; return id;
    },
    async saveProposal(proposal: ProposalDraft) { await (await get()).put('proposals', proposal); },
    async listProposals() { return (await get()).getAll('proposals'); },
    async deleteProposal(id: string) { await (await get()).delete('proposals', id); },
    async mergeRemote(remote: DashboardData, result?: SyncResult) {
      const db = await get(); const tx = db.transaction(['attempts', 'progress', 'bookmarks'], 'readwrite');
      const acknowledgements = new Map(result?.synced.map((row) => [row.id, row]));
      const conflicts = new Set(result?.conflicts.map((row) => row.id));
      for (const server of remote.attempts) {
        const local = await tx.objectStore('attempts').get(server.id); const ack = acknowledgements.get(server.id);
        if (local && sameAttempt(local, server)) {
          await tx.objectStore('attempts').put({ ...local, score: server.score, passed: server.passed, serverVersion: server.serverVersion, syncState: 'synced', serverConflict: undefined });
        } else if (local && ack && local.version === ack.clientVersion) {
          await tx.objectStore('attempts').put(server);
        } else if (local && ack) {
          await tx.objectStore('attempts').put({ ...local, serverVersion: ack.version,
            syncState: (server.serverVersion || 0) > ack.version ? 'conflict' : 'pending',
            serverConflict: (server.serverVersion || 0) > ack.version ? server : undefined });
        } else if (local && local.syncState !== 'synced') {
          await tx.objectStore('attempts').put({ ...local,
            syncState: conflicts.has(server.id) || (server.serverVersion || 0) > (local.serverVersion || 0) ? 'conflict' : local.syncState,
            serverConflict: server });
        } else await tx.objectStore('attempts').put(server);
      }
      for (const server of remote.progress) {
        const local = await tx.objectStore('progress').get(server.key);
        const pendingSeen = Math.max(0, (local?.deviceSeen || 0) - (server.deviceSeen || 0));
        const pendingCorrect = Math.max(0, (local?.deviceCorrect || 0) - (server.deviceCorrect || 0));
        await tx.objectStore('progress').put({ ...server, seen: server.seen + pendingSeen, correct: server.correct + pendingCorrect,
          deviceSeen: Math.max(local?.deviceSeen || 0, server.deviceSeen || 0), deviceCorrect: Math.max(local?.deviceCorrect || 0, server.deviceCorrect || 0),
          lastAnsweredAt: local && local.lastAnsweredAt > server.lastAnsweredAt ? local.lastAnsweredAt : server.lastAnsweredAt });
      }
      for (const server of remote.bookmarks) {
        const local = await tx.objectStore('bookmarks').get(server.key);
        if (!local || bookmarkOrder(server) > bookmarkOrder(local)) await tx.objectStore('bookmarks').put(server);
      }
      await tx.done;
      changed(userId, 'remote');
    },
    async resolveConflict(id: string, keepBoth: boolean) {
      const db = await get(); const tx = db.transaction('attempts', 'readwrite'); const local = await tx.store.get(id);
      if (!local?.serverConflict) return;
      if (keepBoth) await tx.store.put({ ...local, id: crypto.randomUUID(), serverVersion: 0, serverConflict: undefined, syncState: 'pending' });
      await tx.store.put(local.serverConflict); await tx.done;
      changed(userId, 'local');
    },
    async importGuest(source: 'guest' | 'legacy' = 'guest') {
      if (!userId) return;
      const guest = await accountStore(source === 'legacy' ? 'legacy' : null).snapshot(); const db = await get();
      const tx = db.transaction(['attempts', 'progress', 'bookmarks', 'settings'], 'readwrite');
      const imports = (await tx.objectStore('settings').get(source + '-imports'))?.value as Record<string, { seen: number; correct: number }> || {};
      const attemptImports = (await tx.objectStore('settings').get(source + '-attempts'))?.value as Record<string, { id: string; source: Attempt }> || {};
      for (const attempt of guest.attempts) {
        const imported = attemptImports[attempt.id];
        if (imported && sameAttempt(imported.source, attempt)) continue;
        const existing = await tx.objectStore('attempts').get(imported?.id || attempt.id);
        const safeToUpdate = existing && sameAttempt(existing, imported?.source || attempt);
        const id = safeToUpdate ? existing.id : crypto.randomUUID();
        if (!existing || !sameAttempt(existing, attempt)) await tx.objectStore('attempts').put({ ...attempt, id,
          version: safeToUpdate ? Math.max(existing.version + 1, attempt.version) : attempt.version,
          syncState: 'pending', serverVersion: safeToUpdate ? existing.serverVersion : 0, serverConflict: undefined });
        attemptImports[attempt.id] = { id, source: attempt };
      }
      for (const row of guest.progress) {
        const already = imports[row.key] || { seen: 0, correct: 0 };
        const seen = Math.max(0, row.seen - already.seen); const correct = Math.max(0, row.correct - already.correct);
        const local = await tx.objectStore('progress').get(row.key);
        await tx.objectStore('progress').put({ ...row, seen: (local?.seen || 0) + seen, correct: (local?.correct || 0) + correct,
          deviceSeen: (local?.deviceSeen || 0) + seen, deviceCorrect: (local?.deviceCorrect || 0) + correct });
        imports[row.key] = { seen: row.seen, correct: row.correct };
      }
      for (const row of guest.bookmarks) {
        const normalized = { ...row, deleted: row.deleted || false, changeId: row.changeId || '00000000-0000-0000-0000-000000000000' };
        const existing = await tx.objectStore('bookmarks').get(row.key);
        if (!existing || bookmarkOrder(normalized) > bookmarkOrder(existing)) await tx.objectStore('bookmarks').put(normalized);
      }
      await tx.objectStore('settings').put({ key: source + '-imports', value: imports });
      await tx.objectStore('settings').put({ key: source + '-attempts', value: attemptImports }); await tx.done;
      changed(userId, 'local');
    },
    async clear() {
      localStorage.setItem(epochKey(userId), crypto.randomUUID());
      if (userId && userId !== 'legacy') localStorage.setItem(pauseKey(userId), 'true');
      changed(userId, 'cleared');
      const db = await database(userId); const tx = db.transaction(['attempts', 'progress', 'bookmarks', 'proposals', 'settings'], 'readwrite');
      for (const name of tx.objectStoreNames) await tx.objectStore(name).clear(); await tx.done;
      if (userId && userId !== 'legacy') {
        const prefix = 'owner-review:' + userId + ':';
        const keys = Array.from({ length:localStorage.length },(_,i) => localStorage.key(i));
        for (const key of keys) if (key?.startsWith(prefix)) localStorage.removeItem(key);
      }
      changed(userId, 'remote');
    }
  };
}

export const localStore = {
  setAccount(userId: string | null) { activeUser = userId; },
  async saveAttempt(attempt: Attempt, previous?: Attempt) {
    const userId = activeUser; const db = await database(userId); const tx = db.transaction('attempts', 'readwrite');
    const current = await tx.store.get(attempt.id);
    // The screen can still hold an earlier base version after a background acknowledgement.
    // Reuse that acknowledgement only when the screen's previous payload matches storage.
    if (current && previous) {
      const matches = sameAttempt(current, previous);
      attempt = { ...attempt, version: Math.max(attempt.version, current.version + 1),
        serverVersion: matches ? current.serverVersion : attempt.serverVersion,
        syncState: !matches || current.syncState === 'conflict' ? 'conflict' : 'pending',
        serverConflict: matches ? current.serverConflict : current.serverConflict || current };
    }
    await tx.store.put(attempt); await tx.done; changed(userId, 'local');
  },
  async getAttempt(id: string) { return (await currentDatabase()).get('attempts', id); },
  async listAttempts() { return (await currentDatabase()).getAll('attempts'); },
  async deleteAttempt(id: string) { await (await currentDatabase()).delete('attempts', id); },
  async recordAnswer(progress: ProgressRecord, isCorrect: boolean) {
    const userId = activeUser; const db = await database(userId); const tx = db.transaction('progress', 'readwrite');
    const current = await tx.store.get(progress.key);
    await tx.store.put({
      ...progress,
      seen: (current?.seen || 0) + 1,
      correct: (current?.correct || 0) + (isCorrect ? 1 : 0),
      deviceSeen: (current?.deviceSeen ?? current?.seen ?? 0) + 1,
      deviceCorrect: (current?.deviceCorrect ?? current?.correct ?? 0) + (isCorrect ? 1 : 0)
    });
    await tx.done;
    changed(userId, 'local');
  },
  async listProgress() { return (await currentDatabase()).getAll('progress'); },
  async toggleBookmark(track: Track, questionId: string) {
    const userId = activeUser; const db = await database(userId); const tx = db.transaction('bookmarks', 'readwrite');
    const key = track + ':' + questionId;
    const existing = await tx.store.get(key); const deleted = Boolean(existing && !existing.deleted);
    await tx.store.put({ key, track, questionId, deleted, updatedAt: new Date(Math.max(Date.now(), existing ? new Date(existing.updatedAt).getTime() + 1 : 0)).toISOString(), changeId: crypto.randomUUID() });
    await tx.done; changed(userId, 'local'); return !deleted;
  },
  async listBookmarks(track?: string) {
    const all = await (await currentDatabase()).getAll('bookmarks');
    return all.filter((row) => !row.deleted && (!track || row.track === track));
  },
  async setSetting(key: string, value: unknown) { await (await currentDatabase()).put('settings', { key, value }); },
  async getSetting<T>(key: string) { return (await currentDatabase()).get('settings', key).then((entry) => entry?.value as T | undefined); },
  async clearPrivateView() { sessionStorage.removeItem('przedrejsem:last-account'); }
};
